import { useCallback, useEffect, useRef, useState } from 'react';
import { Check } from 'lucide-react';
import { useNavigateWithLoader } from '@/shared/features/navigation-loader';
import {
  buildPdpStoreCatalogUrl,
  buildPdpStoresCatalogUrlFromSelection,
} from '@/features/plp/utils/plpUrl';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/shared/widgets/dialog';
import { cn } from '@/shared/utils/cn';
import { facebookFindLocation } from '@/shared/features/physical-store/presentation/analytics/facebookFindLocation';
import type { PdpDeliveryStoreItemVm } from '../viewModels/pdpDelivery.vm';
import type { PdpStoreSelectedAnalytics } from '../hooks/usePdpAnalytics';

interface PdpNearbyStoresModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  stores: PdpDeliveryStoreItemVm[];
  onStoreSelected?: (selection: PdpStoreSelectedAnalytics) => void;
}

const MAX_SELECTED = 5;

export function PdpNearbyStoresModal({
  open,
  onOpenChange,
  stores,
  onStoreSelected,
}: PdpNearbyStoresModalProps) {
  const navigate = useNavigateWithLoader();
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  const toggleStore = useCallback((storeId: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(storeId)) {
        next.delete(storeId);
      } else if (next.size >= MAX_SELECTED) {
        setToastMessage('Please select upto 5 stores');
        setTimeout(() => setToastMessage(null), 2000);
        return prev;
      } else {
        next.add(storeId);
      }
      return next;
    });
  }, []);

  const findLocationResultsFiredRef = useRef(false);
  useEffect(() => {
    if (!open) {
      findLocationResultsFiredRef.current = false;
      return;
    }
    if (findLocationResultsFiredRef.current) return;
    findLocationResultsFiredRef.current = true;
    facebookFindLocation({});
  }, [open, stores.length]);

  const handleViewDesigns = useCallback(() => {
    if (selectedIds.size === 0) return;
    const selectedStores = stores.filter((store) => selectedIds.has(store.storeId));
    if (selectedStores.length > 0) {
      onStoreSelected?.({
        storeName: selectedStores.map((store) => store.storeName).join(','),
        city: selectedStores.map((store) => store.cityName).join(','),
        storeIds: selectedStores.map((store) => store.storeId),
      });
    }
    const url = buildPdpStoresCatalogUrlFromSelection(selectedStores);
    if (!url) return;
    onOpenChange(false);
    navigate(url);
  }, [navigate, onOpenChange, onStoreSelected, selectedIds, stores]);

  const handleViewDesignsForStore = useCallback(
    (store: PdpDeliveryStoreItemVm) => {
      onStoreSelected?.({
        storeName: store.storeName,
        city: store.cityName,
        storeIds: [store.storeId],
      });
      const url = buildPdpStoreCatalogUrl({
        storeName: store.storeName,
        cityName: store.cityName,
        storeId: store.storeId,
      });
      if (!url) return;
      onOpenChange(false);
      navigate(url);
    },
    [navigate, onOpenChange, onStoreSelected]
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton
        className="max-h-[80vh] max-w-2xl gap-0 overflow-hidden p-0 sm:max-w-2xl"
      >
        <DialogHeader className="space-y-0 bg-[#f0f0f0] px-4 py-3 text-center">
          <DialogTitle className="text-sm font-medium uppercase tracking-wide text-gray-700">
            Select / un-select stores
          </DialogTitle>
        </DialogHeader>

        <div className="max-h-[60vh] overflow-y-auto p-2.5">
          <div className="grid grid-cols-2 gap-2">
            {stores.map((store) => {
              const isSelected = selectedIds.has(store.storeId);
              return (
                <div
                  key={store.storeId}
                  role="button"
                  tabIndex={0}
                  onClick={() => toggleStore(store.storeId)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      toggleStore(store.storeId);
                    }
                  }}
                  className={cn(
                    'relative cursor-pointer rounded-[7px] border p-3 pl-10 text-left text-[13px] leading-[18px]',
                    isSelected ? 'border-[#59ac59]' : 'border-gray-300'
                  )}
                >
                  <span
                    className={cn(
                      'absolute left-2.5 top-[15px] flex h-[18px] w-[18px] items-center justify-center rounded-full border',
                      isSelected
                        ? 'border-[#59ac59] bg-[#59ac59] text-white'
                        : 'border-gray-300 bg-transparent text-transparent'
                    )}
                  >
                    <Check className="h-3 w-3" strokeWidth={3} />
                  </span>
                  <p className="mb-1 font-primary font-semibold">{store.storeName}</p>
                  <p className="font-medium text-gray-900">{store.storeAddress}</p>
                  {store.contactNumber && (
                    <p className="font-medium text-gray-900">
                      Phone:{' '}
                      <a
                        href={`tel:${store.contactNumber}`}
                        onClick={(e) => e.stopPropagation()}
                        className="text-[var(--link-blue)]"
                      >
                        {store.contactNumber}
                      </a>
                    </p>
                  )}
                  <div className="pt-2.5">
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        handleViewDesignsForStore(store);
                      }}
                      className="rounded bg-[#ebe9ea] px-2 py-1.5 text-[12px] uppercase text-[var(--link-blue)]"
                    >
                      View designs for this store
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {toastMessage && (
          <p className="px-4 text-center text-xs text-orange-500">{toastMessage}</p>
        )}

        <div className="border-t border-gray-200">
          <button
            type="button"
            onClick={handleViewDesigns}
            disabled={selectedIds.size === 0}
            className={cn(
              'w-full py-3 text-[13px] font-medium uppercase',
              selectedIds.size > 0
                ? 'bg-[#f15d4c] text-white hover:bg-[#f15d4c]/90'
                : 'bg-[#f0f0f0] text-gray-400'
            )}
          >
            View all Designs in Stores
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
